import { useState } from "react";
import { X, CreditCard, Banknote, Wallet, Check, Percent } from "lucide-react";

interface TakePaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPayment?: (data: { amount: number; method: string; notes: string }) => void;
  treatment?: {
    clientName: string;
    treatmentName: string;
    totalPrice: number;
    paidAmount: number;
  };
}

const paymentMethods = [
  { id: "card", label: "Card", icon: CreditCard },
  { id: "cash", label: "Cash", icon: Banknote },
  { id: "bank_transfer", label: "Bank Transfer", icon: Wallet },
];

export default function TakePaymentModal({
  isOpen,
  onClose,
  onPayment,
  treatment,
}: TakePaymentModalProps) {
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [isFullPayment, setIsFullPayment] = useState(true);

  const balance = treatment ? treatment.totalPrice - treatment.paidAmount : 0;

  const handleAmountChange = (value: string) => {
    // Only allow numbers and one decimal point
    if (/^\d*\.?\d{0,2}$/.test(value)) {
      setAmount(value);
      setIsFullPayment(false);
    }
  };

  const handleFullPayment = () => {
    setIsFullPayment(true);
    setAmount(balance.toFixed(2));
  };

  const handleQuickAmount = (value: number) => {
    setAmount(value.toFixed(2));
    setIsFullPayment(false);
  };

  const handlePayment = () => {
    const paymentAmount = parseFloat(amount) || 0;
    if (paymentAmount <= 0) return;

    onPayment?.({
      amount: paymentAmount,
      method: paymentMethod,
      notes,
    });
    resetForm();
    onClose();
  };

  const resetForm = () => {
    setPaymentMethod("card");
    setAmount("");
    setNotes("");
    setIsFullPayment(true);
  };

  const paymentAmount = parseFloat(amount) || 0;
  const remainingAfterPayment = balance - paymentAmount;
  const isValid = paymentAmount > 0 && paymentAmount <= balance;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-[#e8e0f0]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#f0ebf5]">
          <div>
            <h2 className="text-lg font-bold text-[#2d2d2d]">Take Payment</h2>
            {treatment && (
              <p className="text-sm text-[#6b6b6b]">
                {treatment.treatmentName} - {treatment.clientName}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-[#f8f5fc] rounded-lg transition-colors text-[#6b6b6b] hover:text-[#3d3d3d]"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-5 py-4 space-y-5">
          {/* Payment Summary */}
          {treatment && (
            <div className="p-4 bg-[#f8f5fc] rounded-lg border border-[#e8e0f0]">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-[#6b6b6b]">Treatment Total:</span>
                  <span className="font-medium text-[#2d2d2d]">£{treatment.totalPrice.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-[#6b6b6b]">Already Paid:</span>
                  <span className="font-medium text-[#0d7a3e]">£{treatment.paidAmount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm pt-2 border-t border-[#e8e0f0]">
                  <span className="font-medium text-[#2d2d2d]">Balance Due:</span>
                  <span className="font-bold text-[#704aaf]">£{balance.toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Amount Input */}
          <div>
            <label className="block text-sm font-medium text-[#2d2d2d] mb-2">
              Payment Amount
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6b6b6b] text-lg">£</span>
              <input
                type="text"
                value={amount}
                onChange={(e) => handleAmountChange(e.target.value)}
                placeholder="0.00"
                className="w-full pl-8 pr-4 py-3 border border-[#e8e0f0] rounded-lg text-xl font-semibold text-[#2d2d2d] placeholder-[#c4c4c4] focus:outline-none focus:ring-2 focus:ring-[#e8e0f0] focus:border-[#704aaf] transition-all text-right"
              />
            </div>

            {/* Quick Amount Buttons */}
            <div className="flex gap-2 mt-3">
              <button
                onClick={handleFullPayment}
                className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors ${
                  isFullPayment
                    ? "bg-[#704aaf] text-white"
                    : "bg-[#f8f5fc] text-[#704aaf] border border-[#e8e0f0] hover:bg-[#f0ebf5]"
                }`}
              >
                Full (£{balance.toFixed(2)})
              </button>
              <button
                onClick={() => handleQuickAmount(50)}
                className="flex-1 py-2 text-sm font-medium bg-[#f8f5fc] text-[#704aaf] border border-[#e8e0f0] rounded-lg hover:bg-[#f0ebf5] transition-colors"
              >
                £50
              </button>
              <button
                onClick={() => handleQuickAmount(100)}
                className="flex-1 py-2 text-sm font-medium bg-[#f8f5fc] text-[#704aaf] border border-[#e8e0f0] rounded-lg hover:bg-[#f0ebf5] transition-colors"
              >
                £100
              </button>
            </div>
          </div>

          {/* Payment Method */}
          <div>
            <label className="block text-sm font-medium text-[#2d2d2d] mb-2">
              Payment Method
            </label>
            <div className="grid grid-cols-3 gap-2">
              {paymentMethods.map((method) => {
                const Icon = method.icon;
                return (
                  <button
                    key={method.id}
                    onClick={() => setPaymentMethod(method.id)}
                    className={`flex flex-col items-center gap-2 p-3 rounded-lg border transition-colors ${
                      paymentMethod === method.id
                        ? "border-[#704aaf] bg-[#f8f5fc]"
                        : "border-[#e8e0f0] hover:bg-[#faf8fc]"
                    }`}
                  >
                    <Icon className={`w-5 h-5 ${paymentMethod === method.id ? "text-[#704aaf]" : "text-[#6b6b6b]"}`} />
                    <span className={`text-xs font-medium ${paymentMethod === method.id ? "text-[#704aaf]" : "text-[#6b6b6b]"}`}>
                      {method.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-[#2d2d2d] mb-2">
              Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add payment notes..."
              className="w-full px-3 py-2.5 border border-[#e8e0f0] rounded-lg text-sm text-[#3d3d3d] placeholder-[#9b9b9b] resize-none h-16 focus:outline-none focus:ring-2 focus:ring-[#e8e0f0] focus:border-[#704aaf] transition-all"
            />
          </div>

          {/* Remaining Balance Preview */}
          {paymentAmount > 0 && (
            <div className="p-3 bg-[#e6f7ed] border border-[#b8e6cd] rounded-lg">
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-[#0d7a3e]" />
                <span className="text-sm text-[#0d7a3e]">
                  {remainingAfterPayment <= 0
                    ? "This will complete the payment"
                    : `£${remainingAfterPayment.toFixed(2)} will remain after this payment`}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-[#f0ebf5] bg-[#faf8fc]">
          <div className="flex items-center justify-between">
            <div className="text-sm">
              <span className="text-[#6b6b6b]">Paying:</span>
              <span className="ml-2 text-lg font-bold text-[#2d2d2d]">
                £{paymentAmount.toFixed(2)}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-[#6b6b6b] hover:bg-[#f0ebf5] rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handlePayment}
                disabled={!isValid}
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#0d7a3e] text-white text-sm font-medium rounded-lg hover:bg-[#0a6331] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <CreditCard className="w-4 h-4" />
                Process Payment
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


